import React, { useState, useRef, useEffect } from 'react';
import './FormComponents.scss';

const SearchableDropdownInput = ({
  id,
  name,
  label,
  value,
  onChange,
  options = [],
  placeholder = 'Search and select...',
  required = false,
  disabled = false,
  className = '',
  optionValueKey = 'value',
  optionLabelKey = 'label',
  noOptionsMessage = 'No options found',
  maxHeight = '200px',
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Filter options based on search term
  useEffect(() => {
    if (!searchTerm) {
      setFilteredOptions(options);
    } else {
      const filtered = options.filter(option => {
        const label = typeof option === 'object' 
          ? option[optionLabelKey]?.toString().toLowerCase() 
          : option?.toString().toLowerCase();
        return label?.includes(searchTerm.toLowerCase());
      });
      setFilteredOptions(filtered);
    }
  }, [searchTerm, options, optionLabelKey]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Get display value for selected option
  const getDisplayValue = () => {
    if (!value) return '';
    
    const selectedOption = options.find(option => {
      const optionValue = typeof option === 'object' ? option[optionValueKey] : option;
      return optionValue?.toString() === value?.toString();
    });
    
    if (selectedOption) {
      return typeof selectedOption === 'object' 
        ? selectedOption[optionLabelKey] 
        : selectedOption;
    }
    
    return value;
  };

  const handleInputClick = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      setSearchTerm('');
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    if (!isOpen) setIsOpen(true);
  };

  const handleOptionSelect = (option) => {
    const optionValue = typeof option === 'object' ? option[optionValueKey] : option;
    
    // Create synthetic event for consistency with other form components
    const syntheticEvent = {
      target: {
        name,
        value: optionValue,
        type: 'select'
      }
    };
    
    onChange(syntheticEvent);
    setIsOpen(false);
    setSearchTerm('');
    inputRef.current?.blur();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (filteredOptions.length > 0) {
        handleOptionSelect(filteredOptions[0]);
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchTerm('');
      inputRef.current?.blur();
    }
  };

  return (
    <div className={`form-group searchable-dropdown ${className}`} ref={dropdownRef}>
      {label && (
        <label htmlFor={id} className="form-label">
          {label}
          {required && <span className="required-asterisk">*</span>}
        </label>
      )}
      
      <div className="searchable-dropdown-container">
        <input
          ref={inputRef}
          type="text"
          id={id}
          name={name}
          value={isOpen ? searchTerm : getDisplayValue()}
          onChange={handleSearchChange}
          onClick={handleInputClick}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="form-input searchable-input"
          required={required}
          disabled={disabled}
          autoComplete="off"
          {...props}
        />
        
        <div className={`dropdown-arrow ${isOpen ? 'open' : ''}`}>
          <svg width="12" height="8" viewBox="0 0 12 8">
            <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="1.5" fill="none"/>
          </svg>
        </div>
        
        {isOpen && (
          <div 
            className="dropdown-options" 
            style={{ maxHeight }}
          >
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => {
                const optionValue = typeof option === 'object' ? option[optionValueKey] : option;
                const optionLabel = typeof option === 'object' ? option[optionLabelKey] : option;
                const isSelected = optionValue?.toString() === value?.toString();
                
                return (
                  <div
                    key={`${optionValue}-${index}`}
                    className={`dropdown-option ${isSelected ? 'selected' : ''}`}
                    onClick={() => handleOptionSelect(option)}
                  >
                    {optionLabel}
                  </div>
                );
              })
            ) : (
              <div className="dropdown-option no-options">
                {noOptionsMessage}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchableDropdownInput;